import { useEffect, useState } from 'react';
import { fetchNetworkCostMonthlyRates, type NetworkCostMonthlyRates } from '../../services/profitabilityTruthClient';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym: string) => `${SHORT_MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym} ${ym.slice(0, 4)}`;
const gbp = (n: number | null | undefined, dp = 2) =>
  typeof n === 'number' ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })}` : 'Unknown';

/**
 * Network charge per outbound consignment by month: worked out each night from
 * the Xero P&L "Palletways charges" and the outbound consignments booked that
 * month, beside the figure set by hand. Read only: profit still uses the
 * hand-set figure (plan: xero_monthly_rates_plan.md, step 1).
 */
export default function NetworkChargeMonthsTable() {
  const [data, setData] = useState<NetworkCostMonthlyRates | null>(null);
  const rows = data?.months ?? null;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchNetworkCostMonthlyRates()
      .then((r) => { if (live) setData(r); })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, []);

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">
        Network charge per outbound consignment
      </h4>
      <p className="text-xs text-gray-500 mb-2">
        Worked out each night: Xero P&amp;L &quot;Palletways charges&quot; for the month ÷ outbound consignments booked that
        month. A month is provisional until 10 days after it ends. Profit uses the hand-set figure where there is one.
      </p>
      {error ? (
        <p className="text-xs text-amber-300">Could not load the monthly figures: {error}</p>
      ) : rows === null ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-144">
            <thead>
              <tr className="text-gray-500 text-left">
                <th className="py-2 px-2 font-medium border-b border-gray-600">Month</th>
                <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">From Xero</th>
                <th className="py-2 px-2 font-medium border-b border-gray-600">Status</th>
                <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Palletways charges</th>
                <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Outbound</th>
                <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Set by hand</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = r.computed;
                return (
                  <tr key={r.month} className="border-b border-gray-700/50 text-gray-200">
                    <td className="py-1.5 px-2">{monthLabel(r.month)}</td>
                    <td className="py-1.5 px-2 text-right" title={c?.missing}>{c ? gbp(c.per_outbound_gbp) : 'Not worked out yet'}</td>
                    <td className="py-1.5 px-2">
                      {!c ? '' : c.flagged_big_move ? (
                        <span className="text-amber-300" title={c.compared_with ? `Moved more than 20% on ${monthLabel(c.compared_with.month)} (${gbp(c.compared_with.per_outbound_gbp)}). Check before relying on it.` : undefined}>
                          Provisional, big move
                        </span>
                      ) : c.status === 'final' ? 'Final' : 'Provisional'}
                    </td>
                    <td className="py-1.5 px-2 text-right">{c ? gbp(c.palletways_charges_gbp) : ''}</td>
                    <td className="py-1.5 px-2 text-right">{c?.outbound_consignments != null ? c.outbound_consignments.toLocaleString('en-GB') : c ? 'Unknown' : ''}</td>
                    <td className="py-1.5 px-2 text-right">{r.hand_set_per_outbound_gbp != null ? gbp(r.hand_set_per_outbound_gbp) : 'None'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows && rows.some((r) => r.revenue) ? (
        <div className="mt-5">
          <h4 className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">
            Average revenue per job, by direction
          </h4>
          <p className="text-xs text-gray-500 mb-2">
            From the Xero P&amp;L: outbound = &quot;Palletways Direct Customers&quot; ÷ outbound and local jobs; inbound =
            &quot;Palletways Direct Income&quot; ÷ inbound jobs.{' '}
            {!data?.revenue_in_use
              ? 'Profit fills no job revenue today.'
              : data.revenue_in_use.monthly_from_xero === false
                ? `Monthly figures are switched off: profit uses ${gbp(data.revenue_in_use.avg_gbp_per_job)} for every job.`
                : 'Profit fills a job with no revenue of its own with its month\'s figure for its direction, or carries '
                  + 'an earlier final month\'s, as the network charge does. A figure that moved more than 20% on the '
                  + `previous final month is not used until it settles. Jobs we cannot match to a monthly figure (Palletways depots missing, or no Xero figure for their month) use ${gbp(data.revenue_in_use.avg_gbp_per_job)}.`}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-144">
              <thead>
                <tr className="text-gray-500 text-left">
                  <th className="py-2 px-2 font-medium border-b border-gray-600">Month</th>
                  <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Outbound £/job</th>
                  <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Inbound £/job</th>
                  <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Outbound + local jobs</th>
                  <th className="py-2 px-2 font-medium border-b border-gray-600 text-right">Inbound jobs</th>
                  <th className="py-2 px-2 font-medium border-b border-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const v = r.revenue;
                  const outJobs = v && v.outbound_consignments != null ? v.outbound_consignments + (v.local_consignments ?? 0) : null;
                  return (
                    <tr key={r.month} className="border-b border-gray-700/50 text-gray-200">
                      <td className="py-1.5 px-2">{monthLabel(r.month)}</td>
                      <td className="py-1.5 px-2 text-right" title={v?.direct_customers_gbp != null ? `${gbp(v.direct_customers_gbp)} Direct Customers` : undefined}>
                        {v ? gbp(v.outbound_per_job_gbp) : 'Not worked out yet'}
                      </td>
                      <td className="py-1.5 px-2 text-right" title={v?.direct_income_gbp != null ? `${gbp(v.direct_income_gbp)} Direct Income` : undefined}>
                        {v ? gbp(v.inbound_per_job_gbp) : ''}
                      </td>
                      <td className="py-1.5 px-2 text-right">{outJobs != null ? outJobs.toLocaleString('en-GB') : v ? 'Unknown' : ''}</td>
                      <td className="py-1.5 px-2 text-right">{v?.inbound_consignments != null ? v.inbound_consignments.toLocaleString('en-GB') : v ? 'Unknown' : ''}</td>
                      <td className="py-1.5 px-2">
                        {!v ? '' : v.flagged_big_move ? (
                          <span
                            className="text-warning"
                            title={v.compared_with ? `Moved more than 20% on ${monthLabel(v.compared_with.month)} (${gbp(v.compared_with.outbound_per_job_gbp)} outbound, ${gbp(v.compared_with.inbound_per_job_gbp)} inbound). Not used by profit until it settles.` : undefined}
                          >
                            Provisional, big move ({[v.flagged_big_move.outbound ? 'outbound' : '', v.flagged_big_move.inbound ? 'inbound' : ''].filter(Boolean).join(', ')})
                          </span>
                        ) : v.status === 'final' ? 'Final' : 'Provisional'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
